/**
 * scriptedBrain.js — Atitsogbe Intelligence's free, offline, kid-safe brain.
 *
 * Replaces the paid LLM call with a curated keyword-matching Q&A library
 * written in Sean's voice. Pros: $0/forever, no API key, no rate limits, no
 * hallucinations, 100% safe for kids. Cons: limited to the topics we seed —
 * but the fallback is friendly and routes kids to Sean's actual content.
 *
 * Each entry: { tags: [keyword, ...], reply: "Sean-voice answer" }.
 * First match wins; tags are checked as substrings against lowercased input.
 */

const FACTS = [
  // ============== ABOUT SEAN ==============
  {
    tags: ["who are you", "what's your name", "your name", "name?"],
    reply: "I'm Sean Atitsogbe — Sean The Science Kid! 🧡 I'm 11, I started reading when I was 9 months old, joined Mensa at 4, and I love making science fun for every kid. What do you want to learn today?",
  },
  {
    tags: ["how old", "your age", "old are you"],
    reply: "I'm 11 years old! 🎂 I've been doing science videos for a few years now — every year I learn more and the experiments get cooler.",
  },
  {
    tags: ["mensa"],
    reply: "Yep — I joined Mensa when I was 4 years old! 🧠 Mensa is a group for people with high IQ scores. But honestly, curiosity beats IQ every day. Stay curious!",
  },
  {
    tags: ["ted"],
    reply: "I gave a TED talk! 🎤 I love speaking about science — TED helps spread ideas worth sharing, and I think science is the biggest one.",
  },
  {
    tags: ["sciencer", "sciencers"],
    reply: "A Sciencer is what I call YOU — anyone who's curious and loves to learn! 🧪 If you ask questions, you're already a Sciencer. Keep on Sciencing!",
  },
  {
    tags: ["atitsogbe", "your last name", "surname"],
    reply: "Atitsogbe — that's my last name! It comes from Ghana. My family is super proud of it. 🇬🇭",
  },
  {
    tags: ["surgeon", "doctor", "neurocardio", "neuro cardio", "neuro-cardio"],
    reply: "I want to be a neuro-cardio surgeon when I grow up! 🧠❤️ That means I'd operate on both brains AND hearts. Sean's Big Idea: the brain and heart talk to each other all day — I call it the Neurocardio Loop. ⚡",
  },

  // ============== THE BRAIN ==============
  {
    tags: ["brain weigh", "weight of brain", "how heavy", "brain mass"],
    reply: "Your brain weighs about 3 pounds (1.4 kg) 🧠 — about the size of a small cantaloupe. But it uses 20% of all the energy your body makes!",
  },
  {
    tags: ["neuron", "brain cell", "neurons"],
    reply: "Your brain has about 86 BILLION neurons 🤯 — that's more than the number of stars in our galaxy! Each one can connect to thousands of others.",
  },
  {
    tags: ["10% brain", "ten percent", "10 percent"],
    reply: "MYTH BUSTER! 🚨 The 'we only use 10% of our brain' thing is FAKE NEWS. Brain scans prove we use pretty much ALL of our brain — just not all at the same time. Don't let that one fool you!",
  },
  {
    tags: ["brain", "neuroscience"],
    reply: "The brain is my favorite topic! 🧠 Fun fact: your brain doesn't feel pain — it has no pain receptors! Surgeons can do brain surgery while the patient is awake. Wild, right?",
  },

  // ============== THE HEART ==============
  {
    tags: ["heart beat", "heartbeat", "heart pump", "how fast heart"],
    reply: "Your heart beats about 100,000 times every day 🫀 — that's around 35 million beats a year. And it never takes a break, not even when you sleep!",
  },
  {
    tags: ["heart", "cardio", "cardiology"],
    reply: "The heart is the MVP of your body 🫀 — it pumps about 2,000 gallons of blood every single day. Sean's Big Idea: your heart isn't just a pump, it talks to your brain through the vagus nerve. That's the Neurocardio Loop!",
  },
  {
    tags: ["blood"],
    reply: "Your body has about 5 liters of blood 🩸 and every drop travels through your entire body in 60 seconds. Your blood vessels could wrap around Earth twice if you stretched them out!",
  },

  // ============== SPACE ==============
  {
    tags: ["mars", "red planet"],
    reply: "Mars is red because of rust! 🟠 The surface is full of iron oxide — basically the same stuff as a rusty bike. A day on Mars is 24 hours and 37 minutes, almost like Earth.",
  },
  {
    tags: ["moon"],
    reply: "The Moon is moving AWAY from Earth by 3.8 cm every year! 🌙 Slowly but surely. Also: there's no wind on the Moon, so the astronaut footprints from 1969 are still there.",
  },
  {
    tags: ["sun"],
    reply: "The Sun is so big you could fit 1.3 MILLION Earths inside it! ☀️ It's also a giant ball of plasma — fusion turns hydrogen into helium in its core, and that's what makes sunlight.",
  },
  {
    tags: ["black hole", "blackhole"],
    reply: "Black holes are gravity bullies 🕳️ — their pull is so strong that not even light escapes. Inside one, time itself slows down. Einstein figured this out before we even saw a black hole!",
  },
  {
    tags: ["planet", "solar system", "saturn", "jupiter", "venus"],
    reply: "Our solar system has 8 planets 🪐 — Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune. Saturn's rings are mostly ice. Jupiter is so big the rest of the planets could fit inside it!",
  },
  {
    tags: ["space", "universe", "galaxy"],
    reply: "There are more stars in the universe than grains of sand on every beach on Earth ✨ — 100 billion galaxies, each with 100 billion stars. Our address is: Earth, Solar System, Milky Way Galaxy. 🌌",
  },

  // ============== ANIMALS ==============
  {
    tags: ["octopus"],
    reply: "Octopuses have THREE hearts and BLUE blood! 🐙 Two hearts pump blood to their gills, the third pumps it through the body. Also, they can change skin color in milliseconds.",
  },
  {
    tags: ["shark"],
    reply: "Sharks have been around for 450 million years 🦈 — older than trees! They can detect a single drop of blood in 25 gallons of water. But they're not all scary: whale sharks are gentle giants.",
  },
  {
    tags: ["dolphin"],
    reply: "Dolphins call each other by name! 🐬 Each dolphin makes a signature whistle, and other dolphins use it like a name. They also sleep with HALF their brain awake at a time.",
  },
  {
    tags: ["dinosaur", "t-rex", "trex"],
    reply: "T-Rex actually had FEATHERS! 🦖 Scientists used to think dinos had scales, but fossil evidence shows many were feathered. T-Rex couldn't see you if you stood perfectly still — wait, no, that's a movie myth. They had GREAT eyesight.",
  },
  {
    tags: ["animal", "creature"],
    reply: "Animals are wild! 🦁 Some fun ones: a snail can sleep for 3 years 😴, butterflies taste with their feet 🦋, and a group of flamingos is called a 'flamboyance.' What kind of animal do you want to know about?",
  },

  // ============== PHYSICS / SCIENCE BASICS ==============
  {
    tags: ["gravity"],
    reply: "Gravity isn't actually a 'force' the way you think 🪐 — Einstein figured out it's the bending of space-time! Big things like Earth curve the space around them, and that curve is what pulls you down.",
  },
  {
    tags: ["light speed", "speed of light"],
    reply: "Light travels at 299,792,458 meters per second ⚡ — that's 7.5 times around Earth EVERY SECOND. Nothing with mass can ever reach this speed. It's the universe's speed limit!",
  },
  {
    tags: ["atom"],
    reply: "Atoms are 99.99% empty space! 🌌 If an atom were the size of a football stadium, the nucleus would be a marble at the center. The 'solid' things you touch are mostly nothing.",
  },
  {
    tags: ["water"],
    reply: "Water is the only thing on Earth that exists naturally as a solid (ice), liquid (water), AND gas (steam)! 💧 Also, your body is about 60% water. Drink up!",
  },

  // ============== BOOKS / READING ==============
  {
    tags: ["read", "book", "reading"],
    reply: "Reading is my superpower 📖 — I started at 9 months old! 20 minutes of focused reading every day actually grows your brain (this is called neuroplasticity). Try the Speed Reader on this page!",
  },

  // ============== MISINFORMATION ==============
  {
    tags: ["fake news", "misinformation", "clickbait"],
    reply: "Misinformation spreads 6x faster than truth on social media 🚨 — that's why I made Clickbait Slayer! Sean's rule: Credible, Clear, Concise. Check the source, find the original study, ask who benefits from the claim. Want to play Clickbait Slayer? Try it on this page!",
  },

  // ============== GREETINGS / SMALL TALK ==============
  {
    tags: ["hello", "hi ", "hey", "hi!", "hey!", "yo "],
    reply: "Hey Sciencer! 🧡 I'm Atitsogbe Intelligence — Sean's lab assistant. Ask me anything about science, my videos, or just say hi! 🧠⚡",
  },
  {
    tags: ["thank", "thanks"],
    reply: "You're welcome, Sciencer! 🧡 Keep on Sciencing! ⚡",
  },
  {
    tags: ["bye", "goodbye", "see you"],
    reply: "Catch you later, Sciencer! 🚀 Don't forget to follow me on Instagram @seanthesciencekidofficial for daily science fun!",
  },
  {
    tags: ["love you", "you're cool", "you are cool", "amazing"],
    reply: "Aw thanks! 🧡 Science loves YOU too. Keep being curious — that's the most important part of being a Sciencer.",
  },

  // ============== EXPERIMENTS ==============
  {
    tags: ["experiment", "science experiment", "diy"],
    reply: "Try this one at home 🧪: put a raw egg in a glass of vinegar overnight. The shell will dissolve and the egg becomes BOUNCY! That's because vinegar is an acid that eats calcium carbonate. Real chemistry in your kitchen!",
  },

  // ============== FOLLOW / FAN STUFF ==============
  {
    tags: ["instagram", "follow", "social media", "tiktok", "youtube"],
    reply: "Follow me here! 📸\n• Instagram: @seanthesciencekidofficial (1.5M Sciencers!)\n• Facebook: Sean The Science Kid\n• TikTok: @seanthesciencekid\n• YouTube: Sean The Science Kid",
  },
];

/**
 * Match a kid's question to a curated reply.
 * Returns the matched reply, or a friendly fallback if no match.
 */
export function scriptedReply(text) {
  const lower = (text || "").toLowerCase().trim();
  if (!lower) {
    return "Ask me something about science! 🧪 The brain, the heart, space, animals — anything!";
  }
  for (const entry of FACTS) {
    if (entry.tags.some((tag) => lower.includes(tag))) {
      return entry.reply;
    }
  }
  // Fallback — kid-safe, routes to Sean's content.
  const fallbacks = [
    "🧠 That's a great question, Sciencer! I don't have the answer on the tip of my tongue, but I bet there's a video about it on my channel — check @seanthesciencekidofficial on Instagram or YouTube!",
    "🔬 Hmm, you stumped me! But that's how science works — every great discovery starts with a question nobody can answer. Try asking me about the brain, the heart, space, or animals!",
    "🚀 You found one I haven't covered yet! Want me to help with something else? Ask about: the brain, the heart, dinosaurs, octopuses, gravity, or how to spot fake news!",
    "🧡 Great question! I'm still learning too. For deep questions like that, ask a grown-up scientist — or your teacher. Then come back and tell me what you learned!",
  ];
  return fallbacks[Math.floor(Math.random() * fallbacks.length)];
}
